import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  LinearProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  MobileStepper,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  CheckCircle as CompleteIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';

interface Highlight {
  x: number; // percentage (0-100)
  y: number; // percentage (0-100)
  width: number; // percentage (0-100)
  height: number; // percentage (0-100)
  color?: string;
}

interface InteractiveStep {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  highlights?: Highlight[];
}

interface InteractiveContent {
  steps: InteractiveStep[];
  title?: string;
  description?: string;
}

interface InteractiveStepsProps {
  content: InteractiveContent;
  assetBaseUrl?: string;
  onComplete?: () => void;
  onProgress?: (data: { currentStep: number; totalSteps: number; percent: number }) => void;
}

export default function InteractiveSteps({
  content,
  assetBaseUrl = '',
  onComplete,
  onProgress,
}: InteractiveStepsProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { steps, title, description } = content;
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleStepChange = (stepIndex: number) => {
    setCurrentStep(stepIndex);

    // Mark previous steps as completed
    const newCompleted = new Set(completedSteps);
    for (let i = 0; i < stepIndex; i++) {
      newCompleted.add(i);
    }
    setCompletedSteps(newCompleted);

    onProgress?.({
      currentStep: stepIndex,
      totalSteps,
      percent: Math.round(((stepIndex + 1) / totalSteps) * 100),
    });
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1;
      setCompletedSteps((prev) => new Set(prev).add(currentStep));
      handleStepChange(newStep);
    } else {
      // Last step completed
      setCompletedSteps((prev) => new Set(prev).add(currentStep));
      onComplete?.();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      handleStepChange(currentStep - 1);
    }
  };

  const currentStepData = steps[currentStep];
  const imageUrl = currentStepData?.imageUrl
    ? currentStepData.imageUrl.startsWith('http')
      ? currentStepData.imageUrl
      : `${assetBaseUrl}${currentStepData.imageUrl}`
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      {/* Header */}
      {(title || description) && (
        <Box sx={{ mb: 2 }}>
          {title && (
            <Typography variant="h5" gutterBottom>
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      )}

      {/* Progress bar */}
      <Box sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Schritt {currentStep + 1} von {totalSteps}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Stepper - only on desktop */}
      {!isMobile && totalSteps <= 10 && (
        <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
          {steps.map((step, index) => (
            <Step key={step.id} completed={completedSteps.has(index)}>
              <StepButton onClick={() => handleStepChange(index)}>
                {step.title}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      )}

      {/* Content area */}
      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'grey.50',
        }}
      >
        {/* Step title and description */}
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            {currentStepData?.title || `Schritt ${currentStep + 1}`}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentStepData?.description}
          </Typography>
        </Box>

        {/* Image with highlights */}
        {imageUrl && (
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              p: 2,
            }}
          >
            <Box sx={{ position: 'relative', maxWidth: '100%' }}>
              <img
                src={imageUrl}
                alt={currentStepData?.title || `Step ${currentStep + 1}`}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              />
              {/* Highlight overlays */}
              {currentStepData?.highlights?.map((highlight, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'absolute',
                    left: `${highlight.x}%`,
                    top: `${highlight.y}%`,
                    width: `${highlight.width}%`,
                    height: `${highlight.height}%`,
                    border: `3px solid ${highlight.color || theme.palette.primary.main}`,
                    borderRadius: 1,
                    boxShadow: `0 0 0 4px ${highlight.color || theme.palette.primary.main}40`,
                    animation: 'pulse 2s infinite',
                    pointerEvents: 'none',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: `0 0 0 0 ${highlight.color || theme.palette.primary.main}70`,
                      },
                      '70%': {
                        boxShadow: `0 0 0 10px ${highlight.color || theme.palette.primary.main}00`,
                      },
                      '100%': {
                        boxShadow: `0 0 0 0 ${highlight.color || theme.palette.primary.main}00`,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* No image placeholder */}
        {!imageUrl && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">Kein Bild für diesen Schritt</Typography>
          </Box>
        )}
      </Paper>

      {/* Navigation */}
      {isMobile ? (
        <MobileStepper
          variant="dots"
          steps={totalSteps}
          position="static"
          activeStep={currentStep}
          sx={{ mt: 2, bgcolor: 'transparent' }}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={false}
              variant={currentStep === totalSteps - 1 ? 'contained' : 'text'}
              color={currentStep === totalSteps - 1 ? 'success' : 'primary'}
            >
              {currentStep === totalSteps - 1 ? (
                <>
                  Abschliessen
                  <CompleteIcon sx={{ ml: 0.5 }} />
                </>
              ) : (
                <>
                  Weiter
                  <KeyboardArrowRight />
                </>
              )}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              <KeyboardArrowLeft />
              Zurueck
            </Button>
          }
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button
            startIcon={<PrevIcon />}
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            Zurueck
          </Button>
          <Button
            variant={currentStep === totalSteps - 1 ? 'contained' : 'outlined'}
            color={currentStep === totalSteps - 1 ? 'success' : 'primary'}
            endIcon={currentStep === totalSteps - 1 ? <CompleteIcon /> : <NextIcon />}
            onClick={handleNext}
          >
            {currentStep === totalSteps - 1 ? 'Abschliessen' : 'Weiter'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
